import type { EntityTemplateCreateVariables } from "@/entities/entity-template";
import type { AssetTypeTemplateFormProps } from "@/modules/asset-type-template/asset-type-template-form/asset-type-template-form.type";

import { Button, Form, Input, Space } from "antd";
import { useTranslation } from "react-i18next";

import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import { useFormValidation } from "@/modules/form-validation";
import { SidePanelFooter } from "@/modules/side-panel";

const { useForm } = Form;

const AssetTypeTemplateForm = ({
  mutation,
  saveButtonLabel,
  initialValues,
  onMutate,
}: AssetTypeTemplateFormProps) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);
  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    ["name"]
  );

  const onSave = (values: EntityTemplateCreateVariables) => {
    mutation.mutate(values, { onSuccess: onMutateSuccess });
  };

  return (
    <Form
      id="assetTypeTemplateForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
    >
      <Form.Item
        name="name"
        label={t("technicalForm:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input
          placeholder={t("technicalForm:form.name.label")}
          data-testid="name"
        />
      </Form.Item>

      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="assetTypeTemplateForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

export default AssetTypeTemplateForm;
